import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityMonitoringDashboard = ({ securityStats, recentAlerts, onViewDetails }) => {
  const statCards = [
    {
      label: 'محاولات تسجيل دخول فاشلة',
      value: securityStats?.failedLogins,
      icon: 'AlertTriangle',
      color: 'text-[var(--color-error)]',
      bgColor: 'bg-[var(--color-error)]/10',
      trend: securityStats?.failedLoginsTrend,
    },
    {
      label: 'الجلسات النشطة',
      value: securityStats?.activeSessions,
      icon: 'Users',
      color: 'text-[var(--color-success)]',
      bgColor: 'bg-[var(--color-success)]/10',
      trend: securityStats?.activeSessionsTrend,
    },
    {
      label: 'أنشطة مشبوهة',
      value: securityStats?.suspiciousActivities,
      icon: 'Shield',
      color: 'text-[var(--color-warning)]',
      bgColor: 'bg-[var(--color-warning)]/10',
      trend: securityStats?.suspiciousActivitiesTrend,
    },
    {
      label: 'حسابات محظورة',
      value: securityStats?.blockedAccounts,
      icon: 'Lock',
      color: 'text-[var(--color-muted-foreground)]',
      bgColor: 'bg-[var(--color-muted)]',
      trend: securityStats?.blockedAccountsTrend,
    },
  ];

  const alertSeverityColors = {
    critical: 'border-r-4 border-[var(--color-error)] bg-[var(--color-error)]/5',
    high: 'border-r-4 border-[var(--color-warning)] bg-[var(--color-warning)]/5',
    medium: 'border-r-4 border-[var(--color-accent)] bg-[var(--color-accent)]/5',
    low: 'border-r-4 border-[var(--color-muted-foreground)] bg-[var(--color-muted)]',
  };

  const alertIcons = {
    critical: 'AlertOctagon',
    high: 'AlertTriangle',
    medium: 'AlertCircle',
    low: 'Info',
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards?.map((stat, index) => (
          <div
            key={index}
            className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-2 shadow-warm hover:shadow-warm-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-2">
              <div className={`p-1.5 rounded-lg ${stat?.bgColor}`}>
                <Icon name={stat?.icon} size={14} className={stat?.color} />
              </div>
              {stat?.trend && (
                <span className={`flex items-center gap-0.5 text-[10px] font-medium ${
                  stat?.trend > 0 ? 'text-[var(--color-error)]' : 'text-[var(--color-success)]'
                }`}>
                  <Icon name={stat?.trend > 0 ? 'TrendingUp' : 'TrendingDown'} size={10} />
                  {Math.abs(stat?.trend)}%
                </span>
              )}
            </div>
            <h3 className="text-base font-heading font-bold text-[var(--color-foreground)] mb-1">
              {stat?.value}
            </h3>
            <p className="text-[10px] text-[var(--color-muted-foreground)]">{stat?.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
        <div className="p-4 md:p-6 border-b border-[var(--color-border)] flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-[var(--color-foreground)]">
              التنبيهات الأمنية الأخيرة
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
              مراقبة الأنشطة المشبوهة والتهديدات الأمنية
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            onClick={onViewDetails}
          >
            عرض الكل
          </Button>
        </div>

        <div className="p-4 md:p-6">
          <div className="space-y-3">
            {recentAlerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`p-4 rounded-lg ${alertSeverityColors?.[alert?.severity]}`}
              >
                <div className="flex items-start gap-4">
                  <Icon
                    name={alertIcons?.[alert?.severity]}
                    size={20}
                    className={
                      alert?.severity === 'critical' ? 'text-[var(--color-error)]' :
                      alert?.severity === 'high' ? 'text-[var(--color-warning)]' :
                      alert?.severity === 'medium' ? 'text-[var(--color-accent)]' :
                      'text-[var(--color-muted-foreground)]'
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{alert?.title}</h4>
                      <span className="text-xs text-[var(--color-muted-foreground)] whitespace-nowrap">
                        {alert?.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--color-foreground)] mb-2">{alert?.description}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-1 bg-[var(--color-background)] rounded text-xs text-[var(--color-muted-foreground)]">
                        IP: {alert?.ipAddress}
                      </span>
                      {alert?.user && (
                        <span className="px-2 py-1 bg-[var(--color-background)] rounded text-xs text-[var(--color-muted-foreground)]">
                          المستخدم: {alert?.user}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert?.status === 'resolved' ? 'bg-[var(--color-success)] text-white' :
                        alert?.status === 'investigating' ? 'bg-[var(--color-warning)] text-white' :
                        'bg-[var(--color-error)] text-white'
                      }`}>
                        {alert?.statusLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {recentAlerts?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="ShieldCheck" size={48} color="var(--color-success)" className="mx-auto mb-4" />
                <p className="text-[var(--color-muted-foreground)]">لا توجد تنبيهات أمنية حالياً</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitoringDashboard;